import { Container } from "@mui/material";
import {
    Extension,
    FileCopy,
    FindInPage,
    FormatClear,
    GraphicEq,
    Layers,
    LeakRemove,
    MusicVideo,
    Receipt,
    RecordVoiceOver,
    School,
} from "@mui/icons-material";
import IAmdTimeline from "models/IAmdTimeline";
import IAmdTimelineEvent from "models/IAmdTimelineEvent";
import React from "react";
import theme from "Theme/AppTheme";
import Timeline, { TimelineItem } from "./Timeline";

const microserviceIconMap = {
    "File Pre Processor": <FileCopy />,
    "Text Metadata Processor": <FindInPage />,
    "Audio Video Metadata Processor": <MusicVideo />,
    "Extended Metadata Processor": <Extension />,
    "Audio Media Processor": <GraphicEq />,
    "Transcribe Media Processor": <RecordVoiceOver />,
    "RBC Classifier": <School />,
    "Redact Transcription": <FormatClear />,
    "Redact Audio": <LeakRemove />,
    "Index Transcription": <Receipt />,
    "Transcription Batch Producer": <Layers />,
};

type AmdTimelineProps = {
    timeline: IAmdTimeline;
    displayFileName?: boolean;
};

function amdTimelineEventsToRenderableItems(
    events: IAmdTimelineEvent[],
): TimelineItem[] {
    const eventOccurrences = new Map<string, number>();

    return events.map((item) => {
        const color = item.isStartOfMicroservice
            ? theme.palette.gray[400]
            : item.success
            ? theme.palette.success[700]
            : theme.palette.error.main;

        const variant = item.event === null ? "filled" : "outlined";

        const time = new Date(item.time).toLocaleString("en-US", {
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
            day: "numeric",
            month: "short",
            year: "numeric",
            hour12: true,
        });

        let content: string;
        if (item.event) content = item.event;
        else
            content = `${item.microservice} ${
                item.success
                    ? item.isStartOfMicroservice
                        ? "Started"
                        : "Succeeded"
                    : "Failed"
            }`;

        const eventIdentity = [
            item.time,
            item.microservice,
            item.event ?? "microservice",
            item.isStartOfMicroservice ? "start" : "finish",
            item.success ? "success" : "failure",
        ].join("|");
        const occurrence = (eventOccurrences.get(eventIdentity) ?? 0) + 1;
        eventOccurrences.set(eventIdentity, occurrence);

        return {
            id: `${eventIdentity}|${occurrence}`,
            color,
            variant,
            emphasized: item.event === null,
            time,
            icon: microserviceIconMap[item.microservice] ?? <FileCopy />,
            content,
            contentHeader: item.event ? item.microservice : undefined,
        };
    });
}

export default function AmdTimeline({
    timeline,
    displayFileName = true,
}: AmdTimelineProps) {
    const items = React.useMemo(
        () => amdTimelineEventsToRenderableItems(timeline.timelineEvents),
        [timeline.timelineEvents],
    );

    return (
        <Container disableGutters maxWidth={false}>
            <Timeline
                items={items}
                header={displayFileName && timeline.fileName}
            />
        </Container>
    );
}
